import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "helpers/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Badge } from "./badge";
import { ScrollArea } from "./scroll-area";
import { Button } from "./button";
import { Input } from "./input";

export interface SelectOption {
  label: string;
  value: string | number;
  icon?: string;
}

interface SimpleSelectProps {
  options?: SelectOption[];
  value?: SelectOption | SelectOption[] | null;
  onChange?: (value: SelectOption | SelectOption[] | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isMulti?: boolean;
  filterOption?: (option: SelectOption, inputValue: string) => boolean;
  components?: {
    DropdownIndicator?: React.ComponentType;
  };
}

/**
 * SimpleSelect - A simpler select component without cmdk dependency
 * Compatible with react-select's API
 */
export const SimpleSelect = React.forwardRef<
  HTMLButtonElement,
  SimpleSelectProps
>(
  (
    {
      options = [],
      value = null,
      onChange,
      placeholder = "Select...",
      className,
      disabled = false,
      isMulti = false,
      filterOption,
      components,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Normalize value
    const selectedValues = React.useMemo(() => {
      if (!isMulti) {
        return value && !Array.isArray(value) ? [value] : [];
      }
      return Array.isArray(value) ? value : value ? [value] : [];
    }, [value, isMulti]);

    const handleSelect = (option: SelectOption) => {
      if (disabled) return;

      if (!isMulti) {
        onChange?.(option);
        setOpen(false);
        setSearchValue("");
        return;
      }

      const isSelected = selectedValues.some((v) => v.value === option.value);
      const newValue = isSelected
        ? selectedValues.filter((v) => v.value !== option.value)
        : [...selectedValues, option];

      onChange?.(newValue);
    };

    const handleRemove = (optionToRemove: SelectOption) => {
      if (disabled) return;

      if (!isMulti) {
        onChange?.(null);
        return;
      }

      const newValue = selectedValues.filter(
        (v) => v.value !== optionToRemove.value
      );
      onChange?.(newValue);
    };

    const filteredOptions = React.useMemo(() => {
      if (!searchValue) return options;

      if (filterOption) {
        return options.filter((option) => filterOption(option, searchValue));
      }

      return options.filter((option) =>
        option.label?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [options, searchValue, filterOption]);

    const DropdownIndicator = components?.DropdownIndicator;

    React.useEffect(() => {
      if (open && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }, [open]);

    const singleValue = !Array.isArray(value) ? value : null;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "h-auto min-h-[36px] w-full justify-between font-normal",
              !value && !isMulti && "text-gray-500",
              className
            )}
          >
            {isMulti ? (
              <div className="flex flex-wrap gap-1 flex-1">
                {selectedValues.length > 0 ? (
                  selectedValues.map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className="gap-1 py-0.5 px-2 text-xs"
                    >
                      {option.label}
                      <button
                        type="button"
                        className="ml-0.5 rounded-full outline-none hover:bg-primary-200 focus:ring-2 focus:ring-primary-900 focus:ring-offset-1"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemove(option);
                        }}
                      >
                        <X className="h-3 w-3 text-gray-500 hover:text-gray-700" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  <span className="text-gray-500">{placeholder}</span>
                )}
              </div>
            ) : (
              <span className="flex-1 text-left">
                {singleValue ? (
                  <span className="flex items-center">
                    {singleValue.icon && (
                      <img
                        src={singleValue.icon}
                        alt=""
                        className="mr-2 h-4 w-4"
                      />
                    )}
                    {singleValue.label}
                  </span>
                ) : (
                  placeholder
                )}
              </span>
            )}
            {DropdownIndicator ? (
              <DropdownIndicator />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          sideOffset={4}
        >
          <div className="flex flex-col">
            <div className="flex items-center border-b px-2 py-1.5">
              <Input
                ref={searchInputRef}
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8 shadow-none"
              />
            </div>
            <ScrollArea className="max-h-72 flex flex-col overflow-x-hidden">
              <div className="p-1">
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm text-gray-500">
                    No results found.
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = selectedValues.some(
                      (v) => v.value === option.value
                    );
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={cn(
                          "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100",
                          isSelected && "bg-gray-50"
                        )}
                      >
                        {isMulti && (
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-900",
                              isSelected
                                ? "bg-primary-900 text-white"
                                : "opacity-50"
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                        )}
                        {option.icon && (
                          <img
                            src={option.icon}
                            alt=""
                            className="mr-2 h-4 w-4"
                          />
                        )}
                        <span className="flex-1 text-left">{option.label}</span>
                        {!isMulti && isSelected && (
                          <Check className="h-4 w-4 text-primary-900" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

SimpleSelect.displayName = "SimpleSelect";
